import { useEnvironment } from "@keycloak/keycloak-ui-shared";
import {
  PropsWithChildren,
  MouseEvent as ReactMouseEvent,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  matchPath,
  useLinkClickHandler,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { environment, type Environment, type Feature } from "../environment";
import { TFuncKey } from "../i18n";
import { Box, Collapse, List, ListItem, useMediaQuery, useTheme } from "@mui/material";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import HomeIcon from "../icons/Home";
import { pageNavListStyles, StyledListItemButton, StyledListItemIcon, StyledListItemText, StyledParrentList } from "./styles";
import { useMenu } from "../providers/MenuProvider";
import React from "react";
import PopoverMenu from "./components/PopoverMenu";
import { useContentContext } from "../providers/ContentProvider";

type RootMenuItem = {
  id?: string;
  label: TFuncKey;
  path: string;
  isVisible?: keyof Feature;
  modulePath?: string;
};

type MenuItemWithChildren = {
  label: TFuncKey;
  children: MenuItem[];
  isVisible?: keyof Feature;
};

export type MenuItem = RootMenuItem | MenuItemWithChildren;

interface PageNavProps {
  accountType: string;
  isOpen: boolean;
}

export const PageNav = ({accountType, isOpen}: PageNavProps) => {
  const content = useContentContext();
  const context = useEnvironment<Environment>();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('md'));
  const { setIsOpen } = useMenu();

  return (
    <List sx={pageNavListStyles}>
      {content
                ?.filter((menuItem) =>
                  menuItem.isVisible
                    ? context.environment.features[menuItem.isVisible]
                    : true,
                )
                // EPMIGA-55383 hide applications section
                .filter((menuItem) =>
                   menuItem.label !== 'applications'
                ).map((menuItem) => (
        <NavMenuItem key={menuItem.label as string} menuItem={menuItem} accountType={accountType} isOpen={isOpen} {...(isDownLg ? {onCloseMenu: setIsOpen} : {})} />
      ))}
    </List>
  );
};

type NavMenuItemProps = {
  menuItem: MenuItem;
  accountType?: string;
  isOpen?: boolean;
  onCloseMenu?: (prop: boolean) => void;
};
interface TemplateChildProps {
  onShowPopoverMenu: (e: React.MouseEvent<HTMLElement>) => void;
}

const iconMapper: any = {
  dashboard: <HomeIcon />,
  personalInfo: <PersonIcon />,
  groups: <GroupIcon />,
  accountSecurity: <GppGoodOutlinedIcon />,
};

function NavMenuItem({ menuItem, accountType, isOpen, onCloseMenu }: NavMenuItemProps) {
  const { t } = useTranslation();
  const {
    environment: { features },
  } = useEnvironment<Environment>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const isActive = useMemo(
    () => matchMenuItem(pathname, menuItem),
    [pathname, menuItem],
  );

  const collapsMenuHandler = () => {
    setOpen(!open);
  };

  if ("path" in menuItem) {
    return (
      <NavLink path={menuItem.path} isActive={isActive} isOpen={isOpen} label={menuItem.label} onCloseMenu={onCloseMenu}>
        {t(menuItem.label)}
      </NavLink>
    );
  }

  // Menu item with children: render parent label and collapsible sub-items
  const filteredChildren = menuItem.children
    .filter((child) => child.isVisible ? features[child.isVisible] : true);

  return (
    <Box sx={{ width: '100%' }} data-testid={menuItem.label}>
      <PopoverMenu menuItems={filteredChildren} t={t} navigate={navigate}>
        {({ onShowPopoverMenu }: TemplateChildProps) => (
          <StyledListItemButton
            open={isOpen}
            sx={{ marginBottom: '4px' }}
            onClick={(event) => isOpen ? collapsMenuHandler() : onShowPopoverMenu(event)}
          >
            <StyledListItemIcon open={isOpen}>
              {iconMapper[menuItem.label]}
            </StyledListItemIcon>
            <StyledListItemText primary={t(menuItem.label)} open={isOpen} />
            {isOpen && (open ? <ExpandLess /> : <ExpandMore />)}
          </StyledListItemButton>
        )}
      </PopoverMenu>
      {isOpen && <Collapse in={open} timeout="auto" unmountOnExit>
        <StyledParrentList disablePadding>
          {filteredChildren.map((child) => (
            <NavLink
              onCloseMenu={onCloseMenu}
              key={child.label as string}
              path={"path" in child ? child.path : ''}
              isActive={matchMenuItem(pathname, child)}
              isOpen={isOpen}
            >
              {t(child.label)}
            </NavLink>
          ))}
        </StyledParrentList>
      </Collapse>}
    </Box>
  );
}

function getFullUrl(path: string) {
  return `${new URL(environment.baseUrl).pathname}${path}`;
}

function matchMenuItem(currentPath: string, menuItem: MenuItem): boolean {
  if ("path" in menuItem) {
    return !!matchPath(getFullUrl(menuItem.path), currentPath);
  }

  return menuItem.children.some((child) => matchMenuItem(currentPath, child));
}

type NavLinkProps = {
  path: string;
  isActive: boolean;
  isOpen?: boolean;
  label?: string;
  onCloseMenu?: (prop: boolean) => void;
};

export const NavLink = ({
  path,
  isActive,
  isOpen = true,
  children,
  label,
  onCloseMenu
}: PropsWithChildren<NavLinkProps>) => {
  const menuItemPath = getFullUrl(path) + location.search;
  const handleClick = useLinkClickHandler(menuItemPath);

  return (
    <ListItem
      key={path}
      disablePadding
      sx={{
        display: 'block',
        mb: .5,
      }}
      data-testid={path}
    >
      <StyledListItemButton
        onClick={(event) => {
          handleClick(event as unknown as ReactMouseEvent<HTMLAnchorElement>);
          
          if (onCloseMenu) {
            onCloseMenu(false);
          }
        }}
        selected={isActive}
        open={isOpen}
      >
            <StyledListItemIcon open={isOpen}>
              {label && iconMapper[label]}
            </StyledListItemIcon>
            <StyledListItemText primary={children} open={isOpen} />
          </StyledListItemButton>
        </ListItem>
      );
};
